import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const LOGO_URL = "/clinic-logo.webp";

export default function Login() {
  const { login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(email.trim(), password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: image / brand panel */}
      <div className="relative hidden lg:block">
        <img
          src="/login-hero.webp"
          alt="Lucid Dental clinic"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="inline-flex items-center bg-white rounded-xl px-4 py-2.5 shadow-sm">
            <img src={LOGO_URL} alt="Lucid Dental" className="h-8 w-auto" />
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-bold leading-tight">
              Never miss a follow-up again.
            </h1>
            <p className="mt-3 text-white/85">
              Track every patient, treatment status and next visit in one clean
              dashboard your whole team can use.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="grid-backdrop flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md p-8 border-slate-200 shadow-sm">
          <div className="lg:hidden flex items-center mb-6">
            <img src={LOGO_URL} alt="Lucid Dental" className="h-8 w-auto" />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">
            Welcome back
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Sign in to manage today&apos;s patient follow-ups.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@clinic.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs text-sky-600 hover:text-sky-700 font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-xs text-slate-500 text-center">
            Access is invite-only. Contact your clinic owner for an account.
          </p>
        </Card>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Forgot your password?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-3">
            <p>
              For security, only your clinic owner can reset passwords.
            </p>
            <p>
              Please contact <span className="font-medium text-slate-900">Dr. Priyank Pareek</span> and
              ask them to reset it from the Team Members panel. They&apos;ll share the new
              password with you privately.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setForgotOpen(false)} className="bg-sky-500 hover:bg-sky-600 text-white">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
